import { AfterViewInit, Component, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { InstanaService } from './shared/services/instana/instana.service';
import Pkg from '../../package.json';
import { Select, Store } from '@ngxs/store';
import {
  EMPTY,
  Observable,
  catchError,
  combineLatest,
  combineLatestWith,
  concatMap,
  debounceTime,
  filter,
  iif,
  map,
  merge,
  mergeMap,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { TasksState } from './store/tasks/tasks.state';
import { IAddTaskPayload, ITasksState } from './store/tasks/tasks.interface';
import {
  ClearTasks,
  CompleteTask,
  ErrorTask,
  RemoveTask,
  Retry,
  StartTask,
} from './store/tasks/tasks.actions';
import { SetActiveRoute } from './core/store/config/config.actions';
import { FileUploadService } from './shared/services/file-upload/file-upload.service';
import { LoadingService } from './shared/services/loading.service';

// eslint-disable-next-line @typescript-eslint/naming-convention
declare let _APP_VERSION: string;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.sass'],
  providers: [FileUploadService],
})
export class AppComponent implements OnInit, AfterViewInit {
  @Select(TasksState)
  public tasksState$: Observable<ITasksState>;
  loadingMessage$ = this.loadingService.loadingMessage$;

  tasks$: Observable<IAddTaskPayload[]>;

  currentFilename$: Observable<string>;
  resumeTasks$: Observable<boolean>;

  constructor(
    private _instanaService: InstanaService,
    private _router: Router,
    private _store: Store,
    private _fileUploadService: FileUploadService,
    private loadingService: LoadingService
  ) {
    this._router.events.subscribe((event: any) => this._setCurrentStep(event));
    _APP_VERSION = Pkg.version;
    this.tasks$ = this._store.select((state) => state.tasks.tasks);
    this.currentFilename$ = this._store.select((state) => state.tasks.currentFilename);
    this.resumeTasks$ = this._store.select((state) => state.tasks.resume);
  }

  ngAfterViewInit(): void {
    this._startTaskListWatch();
    this._startCurrentUploadWatch();
  }

  ngOnInit() {
    this._instanaService.setIgnoreUrls();
    this._instanaService.setMetaTag('app_version', _APP_VERSION);
  }

  private _startTaskListWatch() {
    this.tasks$
      .pipe(
        switchMap((tasks) =>
          this.currentFilename$.pipe(
            filter((filename) => filename === ''),
            switchMap(() => this.resumeTasks$),
            filter((resume) => resume === true),
            switchMap(() => tasks.filter((t) => !t.completed).reverse()),
            concatMap((task) => this.processTask(task))
          )
        )
      )
      .subscribe();
  }

  private _startCurrentUploadWatch() {
    this.currentFilename$
      .pipe(
        combineLatestWith(this.resumeTasks$),
        filter(([filename, resume]) => resume === true && filename !== ''),
        switchMap(([filename]) =>
          this.tasks$.pipe(
            map((tasks) => tasks.find((t) => t.name === filename && !t.completed)),
            filter((selectedTask) => selectedTask !== null),
            map((selectedTask) => this.processTask(selectedTask))
          )
        )
      )
      .subscribe();
  }

  private processTask(lastAddedTask: IAddTaskPayload) {
    return this._store.dispatch(new StartTask()).pipe(
      switchMap(() =>
        this._fileUploadService
          .upload({
            preSignedRequest: lastAddedTask.preSignedRequest,
            preSignedApiService: lastAddedTask.getPresignedApiUrlService,
            file: lastAddedTask.file,
            updateTradeGalleryRequest: lastAddedTask.updateTradeGalleryRequest,
            updateTradeGalleryApiService: lastAddedTask.updateGalleryApiUrlService,
            returnMessage: {
              success: lastAddedTask.successMessage,
              error: lastAddedTask.errorMessage,
            },
          })
          .pipe(
            switchMap(() => this._store.dispatch(new CompleteTask(lastAddedTask))),
            catchError(() => this._store.dispatch(new ErrorTask(lastAddedTask)))
          )
      )
    );
  }

  clearTasks() {
    this._store.dispatch(new ClearTasks());
  }

  retry(filenameEvent: string) {
    this._store.dispatch(new Retry(filenameEvent));
  }
  remove(filenameEvent: string) {
    this._store.dispatch(new RemoveTask(filenameEvent));
  }

  private _setCurrentStep(event: NavigationEnd) {
    if (event instanceof NavigationEnd) {
      this._setCurrentActiveProgramByUrl(event.url);
      this._instanaService.setCurrentStep(event.url.split('?')[0]);
    }
  }
  private _setCurrentActiveProgramByUrl(url: string) {
    if (url.indexOf('ips') >= 0) {
      this._store.dispatch(new SetActiveRoute('ips'));
    } else if (url.indexOf('tvf') >= 0) {
      this._store.dispatch(new SetActiveRoute('tvf'));
    } else if (url.indexOf('tvo') >= 0) {
      this._store.dispatch(new SetActiveRoute('tvo'));
    }
  }
}
